/** Fuso da operação CQV. `fromUtc`/`toUtc` na API são instantes UTC. */
export const SAO_PAULO_TIME_ZONE = 'America/Sao_Paulo';

const LOCAL_VALUE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/**
 * Converte valor de `datetime-local` ou data (`YYYY-MM-DD`) em America/Sao_Paulo para ISO UTC.
 * `from`: início do minuto local (ou 00:00:00 do dia civil se data-only / 00:00).
 * `to`: último ms do minuto (ex.: 15:30 → 15:30:59.999). Data-only ou 00:00:00 →
 * 23:59:59.999 **da mesma data civil** (nunca o dia anterior).
 */
export function saoPauloBoundToUtcIso(
  localValue: string | undefined | null,
  bound: 'from' | 'to'
): string | undefined {
  if (!localValue) return undefined;
  const trimmed = localValue.trim();
  if (!trimmed) return undefined;

  const match = LOCAL_VALUE_RE.exec(trimmed);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hasTime = match[4] != null;
  const parsedHour = hasTime ? Number(match[4]) : 0;
  const parsedMinute = hasTime ? Number(match[5]) : 0;
  const parsedSecond = match[6] != null ? Number(match[6]) : 0;
  // Calendário/datetime-local envia T00:00; 00:00 no "até" é o fim do dia escolhido, não meia-noite/dia seguinte.
  const isMidnight =
    parsedHour === 0 && parsedMinute === 0 && parsedSecond === 0;
  const treatAsCivilDay = !hasTime || isMidnight;
  const hour = treatAsCivilDay ? (bound === 'from' ? 0 : 23) : parsedHour;
  const minute = treatAsCivilDay ? (bound === 'from' ? 0 : 59) : parsedMinute;
  const second = bound === 'from' ? 0 : 59;
  const millisecond = bound === 'from' ? 0 : 999;

  return saoPauloWallTimeToUtc(year, month, day, hour, minute, second, millisecond).toISOString();
}

export function saoPauloFilterRangeToUtcIso(
  fromLocal?: string | null,
  toLocal?: string | null
): { fromUtc?: string; toUtc?: string } {
  return {
    fromUtc: saoPauloBoundToUtcIso(fromLocal, 'from'),
    toUtc: saoPauloBoundToUtcIso(toLocal, 'to'),
  };
}

export type SaoPauloPeriodPreset = 'today' | 7 | 14 | 30;

export function saoPauloCivilDateParts(now: Date = new Date()): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SAO_PAULO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const num = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);
  return { y: num('year'), m: num('month'), d: num('day') };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function civilLocalValue(y: number, m: number, d: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}T00:00`;
}

/** Mesma data civil, exibida como fim do dia — `saoPauloBoundToUtcIso('to', ...)` resolve igual a `T00:00`. */
function civilLocalValueEndOfDay(y: number, m: number, d: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}T23:59`;
}

/** Últimos N dias civis inclusive hoje. `to` = T23:59 do último dia (fim do dia civil, para exibição). */
export function saoPauloPresetRange(
  preset: SaoPauloPeriodPreset,
  now: Date = new Date()
): { fromLocal: string; toLocal: string } {
  const today = saoPauloCivilDateParts(now);
  const toLocal = civilLocalValueEndOfDay(today.y, today.m, today.d);
  if (preset === 'today') {
    return { fromLocal: civilLocalValue(today.y, today.m, today.d), toLocal };
  }

  const start = new Date(Date.UTC(today.y, today.m - 1, today.d));
  start.setUTCDate(start.getUTCDate() - (preset - 1));
  return {
    fromLocal: civilLocalValue(start.getUTCFullYear(), start.getUTCMonth() + 1, start.getUTCDate()),
    toLocal,
  };
}

function saoPauloWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  millisecond: number
): Date {
  const wallUtcMs = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  let utcMs = wallUtcMs;
  for (let i = 0; i < 3; i += 1) {
    const offsetMs = timeZoneOffsetMs(SAO_PAULO_TIME_ZONE, new Date(utcMs));
    const next = wallUtcMs - offsetMs;
    if (next === utcMs) break;
    utcMs = next;
  }
  return new Date(utcMs);
}

function timeZoneOffsetMs(timeZone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const num = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);
  let hour = num('hour');
  if (hour === 24) hour = 0;

  const asIfUtc = Date.UTC(
    num('year'),
    num('month') - 1,
    num('day'),
    hour,
    num('minute'),
    num('second')
  );
  const instantSeconds = Date.UTC(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate(),
    instant.getUTCHours(),
    instant.getUTCMinutes(),
    instant.getUTCSeconds()
  );
  return asIfUtc - instantSeconds;
}
